import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addMinutes, isAfter } from 'date-fns';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import InternalHeader from 'components/Navigation/InternalHeader';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';

import { APP_NAME, OPERATIONS, OPERATIONS_AUX } from 'utils/constants';
import { IOperation } from 'utils/interfaces';
import { formatDate } from 'utils/functions';
import { BUTTON_COLORS } from 'utils/enums';
import StorageService from 'utils/storage';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import Modal from 'components/Modal';

export default function MyOperationsPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [operations, setOperations] = useState<Array<IOperation>>([]);

  const [currentBid, setCurrentBid] = useState(0);
  const [currentOffer, setCurrentOffer] = useState(0);
  const [deleteData, setDeleteData] = useState<IOperation | undefined>(undefined);

  const submitDelete = async () => {
    if (!deleteData) return;
    setModalLoading(true);
    let result: any = await generalApi.delete(`/operation/${deleteData._id}`);
    setModalLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Operación eliminada');
    setRefresh(true);
    setDeleteData(undefined);
  };

  useEffect(() => {
    const getOperations = async () => {
      setLoading(true);
      const result = await generalApi.get('/operations');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperations(result.data.operations);
      setLoading(false);
    };
    const getCurrentChange = async () => {
      const result = await generalApi.get('/current_change');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setCurrentBid(result.data.BID);
      setCurrentOffer(result.data.OFFER);
    };
    if (refresh) {
      getOperations();
      setRefresh(false);
    }
    getCurrentChange();
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <div className="min-h-full bg-gray-100 text-[#1B205A]">
        <Modal
          isOpen={deleteData !== undefined}
          onClose={() => {
            setDeleteData(undefined);
          }}
        >
          <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Confirmar acción
              </h3>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <p>¿Está seguro que desea eliminar ésta operación?</p>
          </div>
          <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <SolidButton
              className="w-auto sm:ml-3"
              isLoading={modalLoading}
              onClick={() => submitDelete()}
            >
              Confirmar
            </SolidButton>
            <button
              type="button"
              onClick={() => setDeleteData(undefined)}
              className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancelar
            </button>
          </div>
        </Modal>
        <InternalHeader />
        <div className="pb-8 pt-32 sm:pt-0 2xl:px-24">
          <div className="items-center justify-between space-y-4 border-b border-gray-300 p-6 sm:flex sm:space-y-0 sm:p-12">
            <div className="text-center text-xl sm:text-left">
              <p className="font-bold text-primary-600">Bienvenido</p>
              <p>Nombre: {StorageService.get('userData')?.full_name}</p>
              <p>
                {StorageService.get('userData')?.document_type}:{' '}
                {StorageService.get('userData')?.number}
              </p>
              <p>Correo: {StorageService.get('userData')?.email}</p>
            </div>
            <div className="flex h-[fit-content] max-w-md flex-1 justify-around space-x-8 rounded-lg border border-gray-400 px-8 py-6 text-center">
              <div>
                <p className="text-xl font-bold">{APP_NAME} Compra</p>
                <p className="text-2xl">S/ {currentBid}</p>
              </div>
              <div>
                <p className="text-xl font-bold">{APP_NAME} Vende</p>
                <p className="text-2xl">S/ {currentOffer}</p>
              </div>
            </div>
          </div>
          <div className="justify-between space-y-4 px-8 pt-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Operaciones</p>
            <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => history('/create_operation')}>
              Nuevas Operaciones
            </OutlineButton>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando operaciones"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            columns={[
              {
                title: 'TIPO DE OPERACIÓN',
                dataKey: 'action',
                render: (item: IOperation) => OPERATIONS[OPERATIONS_AUX[item.action]],
              },
              {
                title: 'CÓDIGO',
                dataKey: 'code',
              },
              {
                title: 'SERIE',
                dataKey: 'serie',
                defaultValue: '-',
              },
              {
                title: 'MONTO',
                dataKey: 'amount',
              },
              {
                title: 'TIPO DE CAMBIO',
                dataKey: 'final_rate',
              },
              {
                title: 'BENEFICIARIO',
                dataKey: 'to_name',
              },
              {
                title: 'BANCO DE DESTINO',
                dataKey: 'destiny_bank',
              },
              {
                title: 'FECHA',
                dataKey: 'created_at',
                render: (item: IOperation) =>
                  formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false),
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: IOperation) => {
                  let operationDate = addMinutes(
                    new Date(item.reactivated_at || item.created_at),
                    10
                  );
                  return (
                    <div className="flex space-x-2 pr-2">
                      {item.link_pdf ? (
                        <a href={item.link_pdf} target="_blank" rel="noreferrer">
                          <svg
                            className="text-light-blue hover:text-light-blue-dark mx-auto h-8 w-8"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </a>
                      ) : null}
                      {item.status === '0' && isAfter(operationDate, new Date()) && (
                        <SolidButton
                          color={BUTTON_COLORS.BLUE}
                          onClick={() => history(`/operation/${item._id}`)}
                        >
                          Retomar
                        </SolidButton>
                      )}
                      {item.status === '0' && (
                        <SolidButton color={BUTTON_COLORS.RED} onClick={() => setDeleteData(item)}>
                          Eliminar
                        </SolidButton>
                      )}
                    </div>
                  );
                },
              },
            ]}
            data={operations}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
